import React from 'react';
import { ActivityIndicator, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { FontFamily } from '../../../../../constants/fonts';

interface TablePagerProps {
  /** Zero-based current page. */
  page: number;
  pageSize: number;
  /** Full filtered grouped-row count across all pages, from the backend `total` field. */
  total: number;
  onPageChange: (page: number) => void;
  /** True while the requested page is being fetched — disables the Prev/Next controls. */
  loading?: boolean;
  /** Selectable rows-per-page values — the picker is hidden if this or `onPageSizeChange` is omitted. */
  pageSizeOptions?: number[];
  onPageSizeChange?: (pageSize: number) => void;
}

// Shared 25-rows/page pager used by all 4 Crewing Analytics grouped tables (Location Efficiency,
// Hours by Category, Quality Metrics, Submission Details) so pagination looks/behaves identically.
const TablePager: React.FC<TablePagerProps> = ({ page, pageSize, total, onPageChange, loading, pageSizeOptions, onPageSizeChange }) => {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const currentPage = Math.min(page, totalPages - 1);
  const rangeStart = total === 0 ? 0 : currentPage * pageSize + 1;
  const rangeEnd = Math.min(total, (currentPage + 1) * pageSize);
  const canGoPrev = currentPage > 0 && !loading;
  const canGoNext = currentPage < totalPages - 1 && !loading;
  const showPageSizePicker = !!pageSizeOptions?.length && !!onPageSizeChange;

  return (
    <View style={styles.wrap}>
      <Text style={styles.rangeText}>
        {total === 0 ? 'No records' : `Showing ${rangeStart.toLocaleString()}-${rangeEnd.toLocaleString()} of ${total.toLocaleString()}`}
      </Text>
      <View style={styles.controls}>
        {showPageSizePicker && (
          <View style={styles.pageSizeGroup}>
            <Text style={styles.pageSizeLabel}>Rows:</Text>
            {pageSizeOptions!.map((size) => {
              const isActive = size === pageSize;
              return (
                <TouchableOpacity
                  key={size}
                  style={[styles.pageSizePill, isActive && styles.pageSizePillActive]}
                  onPress={() => !isActive && !loading && onPageSizeChange!(size)}
                  disabled={isActive || loading}
                >
                  <Text style={[styles.pageSizePillText, isActive && styles.pageSizePillTextActive]}>{size}</Text>
                </TouchableOpacity>
              );
            })}
          </View>
        )}
        {loading && <ActivityIndicator size="small" color="#0B5FC6" style={styles.spinner} />}
        <TouchableOpacity
          style={[styles.pagerBtn, !canGoPrev && styles.pagerBtnDisabled]}
          onPress={() => canGoPrev && onPageChange(currentPage - 1)}
          disabled={!canGoPrev}
        >
          <MaterialIcons name="chevron-left" size={18} color={canGoPrev ? '#0B5FC6' : '#B7C4D6'} />
        </TouchableOpacity>
        <Text style={styles.pageLabel}>{`Page ${currentPage + 1} of ${totalPages}`}</Text>
        <TouchableOpacity
          style={[styles.pagerBtn, !canGoNext && styles.pagerBtnDisabled]}
          onPress={() => canGoNext && onPageChange(currentPage + 1)}
          disabled={!canGoNext}
        >
          <MaterialIcons name="chevron-right" size={18} color={canGoNext ? '#0B5FC6' : '#B7C4D6'} />
        </TouchableOpacity>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  wrap: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 10,
    flexWrap: 'wrap',
    gap: 8,
  },
  rangeText: {
    fontSize: 12,
    fontFamily: FontFamily.medium,
    color: '#6B7280',
  },
  controls: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  spinner: {
    marginRight: 4,
  },
  pagerBtn: {
    width: 26,
    height: 26,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#BFDBFE',
    backgroundColor: '#EFF6FF',
    alignItems: 'center',
    justifyContent: 'center',
  },
  pagerBtnDisabled: {
    borderColor: '#E5E7EB',
    backgroundColor: '#F3F4F6',
  },
  pageLabel: {
    fontSize: 12,
    fontFamily: FontFamily.medium,
    color: '#374151',
    minWidth: 90,
    textAlign: 'center',
  },
  pageSizeGroup: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    marginRight: 6,
  },
  pageSizeLabel: {
    fontSize: 12,
    fontFamily: FontFamily.medium,
    color: '#6B7280',
    marginRight: 2,
  },
  pageSizePill: {
    minWidth: 28,
    height: 22,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    backgroundColor: '#FFFFFF',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 4,
  },
  pageSizePillActive: {
    borderColor: '#0B5FC6',
    backgroundColor: '#EFF6FF',
  },
  pageSizePillText: {
    fontSize: 11,
    fontFamily: FontFamily.medium,
    color: '#6B7280',
  },
  pageSizePillTextActive: {
    color: '#0B5FC6',
    fontWeight: '700',
  },
});

export default TablePager;
